import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { callSaradiumBackend } from "../../lib/appwrite";

import { useDesign } from "../../shared/design/DesignProvider";

import type {
  Plan,
} from "../../shared/design/design.types";

import { subscriptionPlans } from "./subscription.constants";

import "./SubscriptionPage.css";

type EntitlementResponse = {
  plan?: Plan;
  developer?: boolean;
};

export function SubscriptionPage() {
  const {
    plan: activePlan,
    refreshEntitlement,
  } = useDesign();

  const [developer, setDeveloper] = useState(false);

  const [changingPlan, setChangingPlan] =
    useState<Plan | null>(null);

  const [message, setMessage] = useState("");

  useEffect(() => {
    async function loadEntitlement() {
      try {
        const response =
          await callSaradiumBackend<EntitlementResponse>({
            action: "getMyEntitlement",
          });

        setDeveloper(Boolean(response.developer));
      } catch {
        setDeveloper(false);
      }
    }

    void loadEntitlement();
  }, []);

  async function selectDeveloperPlan(plan: Plan) {
    setChangingPlan(plan);
    setMessage("");

    try {
      await callSaradiumBackend({
        action: "setDeveloperPlan",
        plan,
      });

      await refreshEntitlement();

      setMessage(
        `Developer plan changed to ${plan.replace("_", " ")}.`,
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Plan could not be changed.",
      );
    } finally {
      setChangingPlan(null);
    }
  }

  return (
    <main className="subscription-page">
      <header className="subscription-page__header">
        <span>SARADIUM MEMBERSHIP</span>

        <h1>Choose your experience.</h1>

        <p>
          Your plan unlocks more planning space and
          interface designs while keeping your account
          secure across Android and web.
        </p>
      </header>

      {developer && (
        <section className="subscription-page__developer-note">
          <strong>Developer account</strong>

          <span>
            You may switch plans here. This is verified
            by the SARADIUM backend, not by the browser.
          </span>
        </section>
      )}

      {message && (
        <p className="subscription-page__message">
          {message}
        </p>
      )}

      <section className="subscription-page__plans">
        {subscriptionPlans.map((item) => {
          const active = activePlan === item.id;
          const loading = changingPlan === item.id;

          return (
            <article
              className={
                active
                  ? "subscription-plan subscription-plan--active"
                  : "subscription-plan"
              }
              key={item.id}
            >
              {item.id === "PREMIUM_PLUS" && (
                <span className="subscription-plan__popular">
                  MOST COMPLETE
                </span>
              )}

              <span className="subscription-plan__label">
                {item.id.replace("_", " ")}
              </span>

              <h2>{item.name}</h2>

              <strong className="subscription-plan__price">
                {item.price}
              </strong>

              {item.studentPrice && (
                <small>{item.studentPrice}</small>
              )}

              <p>{item.description}</p>

              <ul>
                {item.features.map((feature) => (
                  <li key={feature}>✓ {feature}</li>
                ))}
              </ul>

              {developer ? (
                <button
                  className={
                    active
                      ? "subscription-plan__button subscription-plan__button--active"
                      : "subscription-plan__button"
                  }
                  type="button"
                  disabled={active || loading}
                  onClick={() =>
                    void selectDeveloperPlan(item.id)
                  }
                >
                  {active
                    ? "Current plan"
                    : loading
                      ? "Changing…"
                      : `Use ${item.name.replace(
                          "SARADI ",
                          "",
                        )}`}
                </button>
              ) : active ? (
                <button
                  className="subscription-plan__button subscription-plan__button--active"
                  type="button"
                  disabled
                >
                  Current plan
                </button>
              ) : (
                <button
                  className="subscription-plan__button"
                  type="button"
                  disabled
                >
                  Secure payment coming soon
                </button>
              )}
            </article>
          );
        })}
      </section>

      <p className="subscription-page__footnote">
        Need help with your membership?{" "}
        <Link to="/support">
          Contact SARADIUM support.
        </Link>
      </p>
    </main>
  );
}